import { sanityClient } from './client';
import { withSanityImageTransforms } from './image';
import {
  DEFAULT_PROMO_DISCOUNT_PERCENT,
  resolveEffectiveDiscountPercent
} from './marketingCopyConstants';

export { DEFAULT_PROMO_DISCOUNT_PERCENT } from './marketingCopyConstants';

export const MARKETING_COPY_SINGLETON_ID = 'marketingCopy';

export type HeroVideoPreset = 'micah' | 'newApp';

type HeroVideoPresetDefinition = {
  url: string;
  startTime: number;
};

const HERO_VIDEO_PRESETS: Record<HeroVideoPreset, HeroVideoPresetDefinition> = {
  micah: {
    url: '/media/Micah_Landscape_Long_Subs_nobot.mp4',
    startTime: 1
  },
  newApp: {
    url: '/media/Preseason_2026_MicAI_Hero_540p.mp4',
    startTime: 0
  }
};

const isHeroVideoPreset = (value: string | null): value is HeroVideoPreset =>
  value !== null && value in HERO_VIDEO_PRESETS;

export type MarketingCopyAsset =
  | {
      kind: 'video';
      url: string;
      startTime?: number;
    }
  | {
      kind: 'image';
      url: string;
      alt: string;
    };

export interface MarketingCopyHomepageAdvert {
  title: string;
  subtitle: string;
}

export interface MarketingCopyHomepage {
  title: string;
  subtitle: string;
  buttonText: string;
  ctaHref: string;
  asset: MarketingCopyAsset;
  advert: MarketingCopyHomepageAdvert;
}

export interface MarketingCopyPaywallImage {
  url: string;
  alt: string;
}

export interface MarketingCopyPaywall {
  title: string;
  subtitle: string;
  buttonText: string;
  image: MarketingCopyPaywallImage;
}

export interface MarketingCopyTitleSubtitle {
  title: string;
  subtitle: string;
}

export interface MarketingCopyPredictedPointsCard {
  subtitle: string;
}

export interface MarketingCopyVideoAdvert {
  title: string;
  subtitle: string;
  asset: MarketingCopyAsset;
}

export interface MarketingCopy {
  homepage: MarketingCopyHomepage;
  paywall: MarketingCopyPaywall;
  join: MarketingCopyTitleSubtitle;
  memberUpgrade: MarketingCopyTitleSubtitle;
  predictedPointsCard: MarketingCopyPredictedPointsCard;
  videoAdvert: MarketingCopyVideoAdvert;
  /**
   * The EFFECTIVE percentage discount displayed against membership prices
   * sitewide: the Sanity promoDiscountPercent when the promoPricingEnabled
   * toggle is on, 0 when it is off. 0 renders a single full price with no
   * strike-through. While non-zero it must be kept in step with the Stripe
   * default checkout coupon (DEFAULT_CHECKOUT_COUPON, currently
   * preseason2026 at 50%) or the displayed prices will not match what
   * checkout charges (FFH-1746).
   */
  promoDiscountPercent: number;
}

const FALLBACK_TITLE = `Your mini-leagues aren't over`;
const FALLBACK_SUBTITLE = `Start climbing the ranks - cancel anytime`;
const FALLBACK_BUTTON_TEXT = `Get started`;
const FALLBACK_CTA_HREF = '/join';
const FALLBACK_VIDEO_ADVERT_TITLE = 'Results, not features';
const FALLBACK_VIDEO_ADVERT_SUBTITLE =
  'Our users love us because we win them their mini-league.* See a sneak peak of the Hub in our demo video.';
const FALLBACK_HOMEPAGE_ADVERT_TITLE = FALLBACK_VIDEO_ADVERT_TITLE;
const FALLBACK_HOMEPAGE_ADVERT_SUBTITLE = FALLBACK_VIDEO_ADVERT_SUBTITLE;

const FALLBACK_PAYWALL_IMAGE: MarketingCopyPaywallImage = {
  url: '/images/free-trial-2026.jpg',
  alt: 'Rate my team'
};

const FALLBACK_HERO_ASSET: MarketingCopyAsset = {
  kind: 'video',
  url: HERO_VIDEO_PRESETS.micah.url,
  startTime: HERO_VIDEO_PRESETS.micah.startTime
};

export const MARKETING_COPY_FALLBACK: MarketingCopy = {
  homepage: {
    title: FALLBACK_TITLE,
    subtitle: FALLBACK_SUBTITLE,
    buttonText: FALLBACK_BUTTON_TEXT,
    ctaHref: FALLBACK_CTA_HREF,
    asset: FALLBACK_HERO_ASSET,
    advert: {
      title: FALLBACK_HOMEPAGE_ADVERT_TITLE,
      subtitle: FALLBACK_HOMEPAGE_ADVERT_SUBTITLE
    }
  },
  paywall: {
    title: FALLBACK_TITLE,
    subtitle: FALLBACK_SUBTITLE,
    buttonText: FALLBACK_BUTTON_TEXT,
    image: FALLBACK_PAYWALL_IMAGE
  },
  join: {
    title: FALLBACK_TITLE,
    subtitle: FALLBACK_SUBTITLE
  },
  memberUpgrade: {
    title: FALLBACK_TITLE,
    subtitle: FALLBACK_SUBTITLE
  },
  predictedPointsCard: {
    subtitle: FALLBACK_SUBTITLE
  },
  videoAdvert: {
    title: FALLBACK_VIDEO_ADVERT_TITLE,
    subtitle: FALLBACK_VIDEO_ADVERT_SUBTITLE,
    asset: FALLBACK_HERO_ASSET
  },
  promoDiscountPercent: DEFAULT_PROMO_DISCOUNT_PERCENT
};

export const marketingCopyQuery = `*[_id == "${MARKETING_COPY_SINGLETON_ID}"][0]{
  homepage{
    title, subtitle, buttonText, ctaHref,
    assetKind,
    heroVideoPreset,
    "heroImageUrl": heroImage.asset->url,
    "heroImageAlt": heroImage.alt,
    advert
  },
  paywall{
    title, subtitle, buttonText,
    "imageUrl": image.asset->url,
    "imageAlt": image.alt
  },
  join{ title, subtitle },
  memberUpgrade{ title, subtitle },
  predictedPointsCard{ subtitle },
  promoDiscountPercent,
  promoPricingEnabled,
  videoAdvert{
    title,
    subtitle,
    assetKind,
    videoPreset,
    "imageUrl": image.asset->url,
    "imageAlt": image.alt
  }
}`;

type HomepageAssetKind = 'image' | 'video';
type AdvertAssetKind = 'inherit' | HomepageAssetKind;

type RawHomepage = {
  title: string;
  subtitle: string;
  buttonText: string;
  ctaHref: string;
  assetKind: HomepageAssetKind | null;
  heroVideoPreset: string | null;
  heroImageUrl: string | null;
  heroImageAlt: string | null;
  advert: { title: string | null; subtitle: string | null } | null;
};

type RawVideoAdvert = {
  title: string | null;
  subtitle: string | null;
  assetKind: AdvertAssetKind | null;
  videoPreset: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
};

type RawPaywall = {
  title: string;
  subtitle: string;
  buttonText: string;
  imageUrl: string | null;
  imageAlt: string | null;
};

type RawMarketingCopy = {
  homepage: RawHomepage;
  paywall: RawPaywall;
  join: MarketingCopyTitleSubtitle;
  memberUpgrade: MarketingCopyTitleSubtitle;
  predictedPointsCard: MarketingCopyPredictedPointsCard;
  videoAdvert: RawVideoAdvert;
  promoDiscountPercent: number | null;
  promoPricingEnabled: boolean | null;
};

const resolveVideoAsset = (
  presetKey: string | null
): MarketingCopyAsset | null => {
  if (!isHeroVideoPreset(presetKey)) return null;
  const preset = HERO_VIDEO_PRESETS[presetKey];
  return { kind: 'video', url: preset.url, startTime: preset.startTime };
};

// Hero and advert images render at most a content-column wide; anything
// larger than this is wasted bandwidth on the original upload.
const HERO_ADVERT_IMAGE_MAX_WIDTH = 1200;
const PAYWALL_IMAGE_MAX_WIDTH = 800;

const resolveImageAsset = (
  imageUrl: string | null,
  imageAlt: string | null
): MarketingCopyAsset | null => {
  if (!imageUrl) return null;
  return {
    kind: 'image',
    url: withSanityImageTransforms(imageUrl, HERO_ADVERT_IMAGE_MAX_WIDTH),
    alt: imageAlt ?? ''
  };
};

const resolveHomepageAsset = (raw: RawHomepage): MarketingCopyAsset => {
  if (raw.assetKind === 'video') {
    return (
      resolveVideoAsset(raw.heroVideoPreset) ??
      MARKETING_COPY_FALLBACK.homepage.asset
    );
  }
  if (raw.assetKind === 'image') {
    return (
      resolveImageAsset(raw.heroImageUrl, raw.heroImageAlt) ??
      MARKETING_COPY_FALLBACK.homepage.asset
    );
  }
  return MARKETING_COPY_FALLBACK.homepage.asset;
};

const resolveAdvertAsset = (
  raw: RawVideoAdvert,
  homepageAsset: MarketingCopyAsset
): MarketingCopyAsset => {
  if (raw.assetKind === 'video') {
    return resolveVideoAsset(raw.videoPreset) ?? homepageAsset;
  }
  if (raw.assetKind === 'image') {
    return resolveImageAsset(raw.imageUrl, raw.imageAlt) ?? homepageAsset;
  }
  return homepageAsset;
};

const buildMarketingCopy = (raw: RawMarketingCopy): MarketingCopy => {
  const homepageAsset = resolveHomepageAsset(raw.homepage);
  const advertAsset = resolveAdvertAsset(raw.videoAdvert, homepageAsset);

  return {
    homepage: {
      title: raw.homepage.title,
      subtitle: raw.homepage.subtitle,
      buttonText: raw.homepage.buttonText,
      ctaHref: raw.homepage.ctaHref,
      asset: homepageAsset,
      advert: {
        title:
          raw.homepage.advert?.title ?? FALLBACK_HOMEPAGE_ADVERT_TITLE,
        subtitle:
          raw.homepage.advert?.subtitle ?? FALLBACK_HOMEPAGE_ADVERT_SUBTITLE
      }
    },
    paywall: {
      title: raw.paywall.title,
      subtitle: raw.paywall.subtitle,
      buttonText: raw.paywall.buttonText,
      image: raw.paywall.imageUrl
        ? {
            url: withSanityImageTransforms(
              raw.paywall.imageUrl,
              PAYWALL_IMAGE_MAX_WIDTH
            ),
            alt: raw.paywall.imageAlt ?? FALLBACK_PAYWALL_IMAGE.alt
          }
        : FALLBACK_PAYWALL_IMAGE
    },
    join: raw.join,
    memberUpgrade: raw.memberUpgrade,
    predictedPointsCard: raw.predictedPointsCard,
    videoAdvert: {
      title: raw.videoAdvert.title ?? MARKETING_COPY_FALLBACK.videoAdvert.title,
      subtitle:
        raw.videoAdvert.subtitle ?? MARKETING_COPY_FALLBACK.videoAdvert.subtitle,
      asset: advertAsset
    },
    promoDiscountPercent: resolveEffectiveDiscountPercent(
      raw.promoDiscountPercent,
      raw.promoPricingEnabled
    )
  };
};

export const getMarketingCopy = async (): Promise<MarketingCopy> => {
  try {
    const raw = await sanityClient.fetch<RawMarketingCopy | null>(
      marketingCopyQuery
    );
    if (!raw) return MARKETING_COPY_FALLBACK;
    return buildMarketingCopy(raw);
  } catch (error) {
    console.error(
      '[marketingCopy] failed to fetch from Sanity, returning fallback',
      error
    );
    return MARKETING_COPY_FALLBACK;
  }
};
